import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import moment from 'moment';
import { getDateFormat, showToast, getOrganizationData } from '../../../helper';
import { IOrganizationData } from '../../../helper/organizationStorage';
import { IPageInfo } from '../../../components/OATableComponent';
import { DATE_FORMAT_BE } from '../../../constants';

/**
 * Generic API call wrapper that handles try-catch blocks and callbacks
 */
export interface ApiCallOptions<T = any> {
  apiCall: () => Promise<any>;
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  callback?: (response?: any) => void;
}

export async function handleApiCall<T = any>(options: ApiCallOptions<T>): Promise<void> {
  const {
    apiCall,
    onSuccess,
    onError,
    successMessage,
    errorMessage,
    showSuccessToast = false,
    showErrorToast = false,
    callback
  } = options;

  try {
    await apiCall()
      .then((response: any) => {
        if (showSuccessToast && successMessage) {
          showToast(successMessage, 'success');
        }
        onSuccess?.(response);
        callback?.(response);
      })
      .catch(err => {
        if (showErrorToast && errorMessage) {
          showToast(errorMessage, 'error');
        }
        onError?.(err);
        callback?.(err);
      });
  } catch (error: any) {
    callback?.(error);
  }
}

/**
 * Organization data validation result
 */
export interface OrgValidationResult {
  isValid: boolean;
  orgData: IOrganizationData | null;
  orgId: string;
  orgCode: string;
}

/**
 * Validates organization selection from encrypted storage
 * Returns null if validation fails, otherwise returns validation result
 */
export function validateOrganizationSelection(options?: {
  requireSpecific?: boolean;
  showToastOnError?: boolean;
}): OrgValidationResult | null {
  const { requireSpecific = true, showToastOnError = true } = options || {};

  const orgData = getOrganizationData();
  const orgId = orgData?.organizationId || '';
  const orgCode = orgData?.organizationCode || '';

  // Check if "all" organizations is selected
  if (requireSpecific && (orgId === 'all' || !orgData)) {
    if (showToastOnError) {
      showToast('Please select a specific organization to view ledger entries', 'warning');
    }
    return null;
  }

  // Check if organization data is missing
  if (!orgData) {
    if (showToastOnError) {
      showToast('Organization data not found. Please select an organization.', 'error');
    }
    return null;
  }

  return {
    isValid: true,
    orgData,
    orgId: orgId || '',
    orgCode
  };
}

/**
 * Query parameter configuration
 */
export interface QueryParamConfig {
  dateRange?: DateRange<Moment | Date>;
  pageIndex: number;
  dateParamNames?: { start: string; end: string };
  dateFormat?: string;
  includePageSize?: boolean;
  pageSize?: number;
  additionalParams?: Record<string, any>;
  useISOFormat?: boolean;
}

/**
 * Builds a query string from configuration
 * Consolidates all query parameter building logic
 */
export function buildQueryString(config: QueryParamConfig): string {
  const {
    dateRange,
    pageIndex,
    dateParamNames = { start: 'start', end: 'end' },
    dateFormat = DATE_FORMAT_BE,
    includePageSize = true,
    pageSize = 10,
    additionalParams = {},
    useISOFormat = false
  } = config;

  const params = new URLSearchParams();

  // Add date parameters
  if (dateRange && dateRange[0] && dateRange[1]) {
    let startDate: string;
    let endDate: string;

    if (useISOFormat) {
      startDate = moment(dateRange[0] as Date).startOf('day').toISOString();
      endDate = moment(dateRange[1] as Date).endOf('day').toISOString();
    } else {
      startDate = getDateFormat(dateRange[0] as Date, dateFormat);
      endDate = getDateFormat(dateRange[1] as Date, dateFormat);
    }

    params.append(dateParamNames.start, startDate);
    params.append(dateParamNames.end, endDate);
  }

  // Add pagination parameters
  params.append('page', String(pageIndex - 1));
  if (includePageSize) {
    params.append('size', String(pageSize));
  }

  // Add additional parameters (e.g., entryType, customerId, etc.)
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });

  return params.toString();
}

/**
 * Updates page info state with data from API response
 * Standardizes the page info update pattern
 */
export function updatePageInfo(
  currentPageInfo: IPageInfo,
  responseData: any
): IPageInfo {
  return Object.assign({}, currentPageInfo, {
    totalCount: responseData?.totalElements || 0,
    totalPages: responseData?.totalPages || 0,
    count: responseData?.numberOfElements || 0
  });
}